import { HTMLAttributes, PropsWithChildren, useState } from 'react';
import Icon from '../Icon/Icon';
import styles from './Table.module.scss'; // Import the SCSS stylesheet
import { ColumnDef, useTable } from './useTable';
import { cn } from '../../../utils/utils';
import React from 'react';

type TableProps<T> = {
  colDefs: Array<ColumnDef<T>>;
  data: T[];
  isLoading?: boolean;
  isExpandable?: boolean;
  getRowId?: (row: T) => string;
  sortable?: boolean;
  renderSubRow?: (row: T) => React.ReactNode;
};

const getSortIcon = (sort: 'desc' | 'asc' | null) => {
  if (sort === 'asc') return <Icon name="caret-up" size={20} />;
  if (sort === 'desc') return <Icon name="caret-down" size={20} />;
  return null;
};

const Table = <T extends unknown>({
  colDefs,
  data,
  isLoading = false,
  getRowId,
  isExpandable = false,
  renderSubRow,
  sortable = true,
}: TableProps<T>) => {
  const { tableHeaders, tableData, sortedData, onHeaderClick } = useTable({ colDefs, data });
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());

  const toggleRow = (id: string | number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const renderTableData = () => {
    if (isLoading)
      return (
        <tr>
          <td colSpan={colDefs.length}>Loading...</td>
        </tr>
      );

    if (tableData.length === 0)
      return (
        <tr>
          <td colSpan={colDefs.length}>No data found.</td>
        </tr>
      );

    return tableData.map((row, rowIndex) => {
      const isEvenRow = rowIndex % 2 === 1;
      const rowKey = getRowId ? getRowId(data[rowIndex]) : rowIndex;

      const isExpanded = expandedRows.has(rowIndex);

      return (
        <React.Fragment key={rowKey}>
          <tr className={isEvenRow ? styles['row-even'] : styles['row-odd']}>
            {isExpandable && (
              <td
                onClick={() => toggleRow(rowIndex)}
                style={{ cursor: 'pointer' }}
                data-testid={`expand-button-${rowIndex}`}
              >
                <Icon
                  name={isExpanded ? 'caret-down' : 'caret-up'}
                  style={!isExpanded ? { transform: 'rotate(90deg)' } : undefined}
                  size={16}
                />
              </td>
            )}

            {row.map((cell, cellIndex) => (
              <td key={cellIndex}>{cell}</td>
            ))}
          </tr>
          {isExpandable && isExpanded && (
            <tr
              data-testid={`sub-tr-${rowIndex}`}
              className={isEvenRow ? styles['row-even'] : styles['row-odd']}
            >
              <td
                data-testid={`sub-td-${rowIndex}`}
                colSpan={colDefs.length + 1}
                className={styles['subrow']}
              >
                {renderSubRow ? renderSubRow(sortedData[rowIndex]) : null}
              </td>
            </tr>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className={styles['table-container']}>
      <table className={styles['table']}>
        <thead>
          <tr>
            {isExpandable && <th></th>}
            {tableHeaders.map((header, index) => (
              <th
                key={index}
                onClick={() => {
                  sortable && onHeaderClick(index);
                }}
              >
                <div>
                  {header.header} {getSortIcon(header.sort)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{renderTableData()}</tbody>
      </table>
    </div>
  );
};

export const TableActions: React.FC<HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...otherProps
}) => {
  return <div className={cn(styles['table-actions'], className)} {...otherProps} />;
};

export default Table;
